export interface TableSortValue {
  id: string;
  desc: boolean;
}

interface CreateTableSearchParamsOptions {
  page: number;
  pageSize: number;
  sorted: TableSortValue[];
  searchValue?: string;
  filterValue?: Record<string, any>;
  defaultSort?: TableSortValue[];
  defaultFilter?: Record<string, any>;
}

const DEFAULT_SORT: TableSortValue[] = [{ id: "createdAt", desc: true }];
const RESERVED_PARAMS = new Set([
  "page",
  "pageSize",
  "sort",
  "q",
  "search",
  "filter",
]);

const isSortValue = (value: any): value is TableSortValue =>
  typeof value?.id === "string" && typeof value?.desc === "boolean";

export const parseTableSort = (
  searchParams: URLSearchParams,
  defaultSort: TableSortValue[] = DEFAULT_SORT
): TableSortValue[] => {
  const value = searchParams.get("sort");
  if (!value) return defaultSort;
  if (value === "none") return [];

  // Backward compatibility for links created using JSON table state.
  if (value.startsWith("[")) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) && parsed.every(isSortValue)
        ? parsed
        : defaultSort;
    } catch {
      return defaultSort;
    }
  }

  const parsed = value
    .split(",")
    .filter(Boolean)
    .map((sort) => ({
      id: sort.startsWith("-") ? sort.slice(1) : sort,
      desc: sort.startsWith("-"),
    }))
    .filter(({ id }) => id.length > 0);

  return parsed.length ? parsed : defaultSort;
};

export const parseTableSearch = (searchParams: URLSearchParams): string =>
  searchParams.get("q") || searchParams.get("search") || "";

const setNestedValue = (target: Record<string, any>, path: string, value: string) => {
  const keys = path.split(".");
  let current = target;

  keys.forEach((key, index) => {
    if (index === keys.length - 1) {
      current[key] = value;
    } else {
      current[key] = current[key] || {};
      current = current[key];
    }
  });
};

export const parseTableFilters = <T extends Record<string, any>>(
  searchParams: URLSearchParams,
  defaultFilter: T
): T => {
  let filters: T = JSON.parse(JSON.stringify(defaultFilter));
  const legacyFilter = searchParams.get("filter");

  if (legacyFilter) {
    try {
      filters = { ...filters, ...JSON.parse(legacyFilter) };
    } catch {
      // Ignore malformed legacy state and use the page defaults.
    }
  }

  const filterKeys = new Set(Object.keys(defaultFilter));
  searchParams.forEach((value, key) => {
    const rootKey = key.split(".")[0];
    if (!RESERVED_PARAMS.has(key) && filterKeys.has(rootKey)) {
      setNestedValue(filters, key, value);
    }
  });

  return filters;
};

const serializeValue = (value: any): string => {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
};

const appendFilters = (
  params: URLSearchParams,
  value: Record<string, any>,
  defaultValue: Record<string, any> = {},
  prefix = ""
) => {
  Object.entries(value || {}).forEach(([key, childValue]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const defaultChild = defaultValue?.[key];

    if (
      childValue &&
      typeof childValue === "object" &&
      !(childValue instanceof Date)
    ) {
      appendFilters(params, childValue, defaultChild || {}, path);
      return;
    }

    if (
      childValue !== undefined &&
      childValue !== null &&
      childValue !== "" &&
      childValue !== "All" &&
      childValue !== defaultChild
    ) {
      params.set(path, serializeValue(childValue));
    }
  });
};

export const createTableSearchParams = ({
  page,
  pageSize,
  sorted,
  searchValue = "",
  filterValue = {},
  defaultSort = DEFAULT_SORT,
  defaultFilter = {},
}: CreateTableSearchParamsOptions): URLSearchParams => {
  const params = new URLSearchParams();

  if (page > 1) params.set("page", String(page));
  if (pageSize !== 20) params.set("pageSize", String(pageSize));

  const isDefaultSort =
    sorted.length === defaultSort.length &&
    sorted.every(
      (sort, index) =>
        sort.id === defaultSort[index].id && sort.desc === defaultSort[index].desc
    );
  if (!isDefaultSort) {
    params.set(
      "sort",
      sorted.length
        ? sorted.map(({ id, desc }) => `${desc ? "-" : ""}${id}`).join(",")
        : "none"
    );
  }

  const search = searchValue.trim();
  if (search) params.set("q", search);
  appendFilters(params, filterValue, defaultFilter);

  return params;
};
